import { Decoration, ViewPlugin, MatchDecorator } from "@codemirror/view"
import { store, api, mentionPattern, matchedEntity, SIGIL_KINDS } from "./store"
import { fuzzyFilter } from "./fuzzy"

const CREATABLE_KIND = { "@": "character", "+": "location", "#": "thread" }

const mentionDecorator = new MatchDecorator({
  regexp: mentionPattern(),
  decorate: (add, from, to, match) => {
    if (match[1]) return
    const entity = matchedEntity(match)
    if (!entity) return
    const highlighted = store.highlighted.has(entity.id) ? " cm-mention-highlighted" : ""
    add(
      from,
      to,
      Decoration.mark({
        class: `cm-mention cm-mention-${entity.kind}${highlighted}`,
        attributes: { "data-entity-id": String(entity.id) }
      })
    )
  }
})

export const mentionPills = ViewPlugin.fromClass(
  class {
    constructor(view) {
      this.decorations = mentionDecorator.createDeco(view)
    }

    update(update) {
      this.decorations = update.docChanged || update.viewportChanged
        ? mentionDecorator.createDeco(update.view)
        : this.decorations
    }

    refresh(view) {
      this.decorations = mentionDecorator.createDeco(view)
    }
  },
  {
    decorations: (plugin) => plugin.decorations,
    eventHandlers: {
      mousedown(event, view) {
        const pill = event.target.closest(".cm-mention")
        if (!pill || !(event.metaKey || event.ctrlKey)) return false
        const entity = store.findEntity(pill.dataset.entityId)
        if (entity) emitOpenEntity(view, entity)
        return true
      }
    }
  }
)

export function repaintMentions(view) {
  const plugin = view.plugin(mentionPills)
  if (plugin) {
    plugin.refresh(view)
    view.dispatch({})
  }
}

function emitOpenEntity(view, entity) {
  view.dom.dispatchEvent(
    new CustomEvent("ootline:open-entity", { bubbles: true, detail: { url: entity.url, id: entity.id } })
  )
}

export function mentionAtCursor(state) {
  const { head } = state.selection.main
  const line = state.doc.lineAt(head)
  const column = head - line.from
  for (const match of line.text.matchAll(mentionPattern())) {
    const start = match.index
    const end = start + match[0].length
    if (column >= start && column <= end) {
      return matchedEntity(match)
    }
  }
  return null
}

export function followMention(view) {
  const entity = mentionAtCursor(view.state)
  if (!entity) return false
  emitOpenEntity(view, entity)
  return true
}

export function mentionCompletions(context) {
  const match = context.matchBefore(/[@+#](?:\[[^\]\n]*|[\p{L}\p{N}_'-]*)?$/u)
  if (!match) return null

  const sigil = match.text[0]
  if (match.text.length === 1 && sigil === "#" && !context.explicit) return null

  const query = match.text.slice(1).replace(/^\[/, "")
  const kinds = SIGIL_KINDS[sigil]
  const pool = store.entities.filter((entity) => kinds.includes(entity.kind))
  const matches = fuzzyFilter(query, pool, (entity) => entity.name)

  const options = matches.map((entity) => ({
    label: entity.name,
    detail: entity.kind,
    apply: entity.reference
  }))

  const trimmed = query.trim()
  const exactExists = matches.some((entity) => entity.name.toLowerCase() === trimmed.toLowerCase())
  if (trimmed && !exactExists) {
    const kind = CREATABLE_KIND[sigil]
    options.push({
      label: `Create ${kind} “${trimmed}”`,
      detail: "new",
      apply: (view, _completion, from, to) => createAndInsert(view, kind, trimmed, from, to)
    })
  }

  if (!options.length) return null
  return { from: match.from, options, filter: false }
}

async function createAndInsert(view, kind, name, from, to) {
  emitStatus(view, "Saving entity…", true)
  const entity = await api(store.urls.entities, { method: "POST", body: { entity: { name, kind } } })
  store.upsertEntity(entity)
  view.dispatch({
    changes: { from, to, insert: entity.reference },
    selection: { anchor: from + entity.reference.length }
  })
  repaintMentions(view)
  view.focus()
  emitStatus(view, "Saved", false)
}

function emitStatus(view, text, sticky) {
  view.dom.dispatchEvent(
    new CustomEvent("ootline:status", { bubbles: true, detail: { text, sticky } })
  )
}
